import { useEffect, useMemo, useState } from 'react';
import { Header, Card, Button, Select, Input, Badge, DataTable, Column, StatCard, Modal, EmptyState, useToast } from '../../shared/components';
import { TimerIcon, ReportIcon, AuditIcon } from '../../shared/icons';
import { useCurrentUser } from '../../shared/auth/AuthProvider';
import { getTurnoGeoRepository } from '../../shared/services/turnoGeo/repositoryFactory';
import { Employee, EmployeeHours, HoursReport, HoursDayRow } from '../../shared/services/turnoGeo/types';
import { exportHoursReport } from './hoursExcel';
import { ImportMarksModal } from './components/ImportMarksModal';

const repo = getTurnoGeoRepository();
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const n2 = (n: number) => n.toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const money = (n: number) => n.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });
const DOW = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];

function range(kind: 'week' | 'lastWeek' | 'month' | 'lastMonth'): { from: string; to: string } {
  const t = new Date();
  const monday = new Date(t); monday.setDate(t.getDate() - ((t.getDay() + 6) % 7));
  if (kind === 'week') { const s = new Date(monday); s.setDate(s.getDate() + 6); return { from: iso(monday), to: iso(s) }; }
  if (kind === 'lastWeek') { const a = new Date(monday); a.setDate(a.getDate() - 7); const b = new Date(a); b.setDate(b.getDate() + 6); return { from: iso(a), to: iso(b) }; }
  if (kind === 'month') return { from: iso(new Date(t.getFullYear(), t.getMonth(), 1)), to: iso(new Date(t.getFullYear(), t.getMonth() + 1, 0)) };
  return { from: iso(new Date(t.getFullYear(), t.getMonth() - 1, 1)), to: iso(new Date(t.getFullYear(), t.getMonth(), 0)) };
}

/**
 * Horas y recargos: el cálculo del lado de TurnoGeo. Descuenta almuerzo, suma por semana contra el mínimo (42 h),
 * separa extras diurnas/nocturnas/festivas y el recargo nocturno del 35 %. Solo líderes (admin y supervisor);
 * los valores en pesos y la carga de marcaciones, solo administrador. El operativo nunca ve esta pantalla.
 */
export function HoursPage() {
  const toast = useToast();
  const user = useCurrentUser();
  const isAdmin = user.role === 'admin';
  const [from, setFrom] = useState(range('week').from);
  const [to, setTo] = useState(range('week').to);
  const [userId, setUserId] = useState('');
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [report, setReport] = useState<HoursReport | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [detail, setDetail] = useState<EmployeeHours | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [showHow, setShowHow] = useState(false);

  useEffect(() => { repo.listEmployees().then(setEmployees).catch(() => undefined); }, []);

  const generate = async () => {
    setIsLoading(true);
    try { setReport(await repo.getHoursReport({ from, to, userId: userId || undefined })); }
    catch (e) { toast.show(e instanceof Error ? e.message : 'No se pudo generar el reporte.', 'error'); }
    finally { setIsLoading(false); }
  };
  const quick = (k: Parameters<typeof range>[0]) => { const r = range(k); setFrom(r.from); setTo(r.to); };

  const exportExcel = async () => {
    try { const full = await repo.getHoursReport({ from, to, userId: userId || undefined, detail: true }); exportHoursReport(full, isAdmin); }
    catch (e) { toast.show(e instanceof Error ? e.message : 'No se pudo exportar.', 'error'); }
  };

  const totals = useMemo(() => {
    const e = report?.employees ?? [];
    const sum = (f: (x: EmployeeHours) => number) => e.reduce((a, x) => a + f(x), 0);
    return {
      net: sum((x) => x.netHours), extra: sum((x) => x.extraTotal), night: sum((x) => x.nightSurchargeHours), festive: sum((x) => x.festiveHours.total),
      below: e.filter((x) => x.weeksBelowMin > 0).length, open: sum((x) => x.openShifts),
    };
  }, [report]);

  const columns: Column<EmployeeHours>[] = [
    { key: 'name', header: 'Empleado', render: (e) => <div><p className="font-medium">{e.fullName}</p><p className="text-xs text-ink-400">{e.documentNumber ?? ''}</p></div>, sortable: true, sortValue: (e) => e.fullName },
    { key: 'net', header: 'Horas netas', render: (e) => n2(e.netHours), sortable: true, sortValue: (e) => e.netHours },
    { key: 'below', header: 'Semanas < mínimo', render: (e) => (e.weeksBelowMin > 0 ? <Badge tone="warning">{e.weeksBelowMin}</Badge> : <Badge tone="success">0</Badge>), sortable: true, sortValue: (e) => e.weeksBelowMin },
    { key: 'ed', header: 'Extra diurna', render: (e) => n2(e.extra.diurnaOrdinaria) },
    { key: 'en', header: 'Extra nocturna', render: (e) => n2(e.extra.nocturnaOrdinaria) },
    { key: 'efd', header: 'Extra fest. diurna', render: (e) => n2(e.extra.diurnaFestiva) },
    { key: 'efn', header: 'Extra fest. nocturna', render: (e) => n2(e.extra.nocturnaFestiva) },
    { key: 'ns', header: 'Recargo nocturno 35 %', render: (e) => n2(e.nightSurchargeHours), sortable: true, sortValue: (e) => e.nightSurchargeHours },
    { key: 'fh', header: 'Horas festivas', render: (e) => n2(e.festiveHours.total) },
    ...(isAdmin ? [{ key: 'pay', header: 'Estimado ($)', render: (e: EmployeeHours) => (e.pay ? money(e.pay.total) : '—') } as Column<EmployeeHours>] : []),
    { key: 'open', header: 'Alertas', render: (e) => (e.openShifts > 0 ? <Badge tone="critical">{e.openShifts} turno(s) abierto(s)</Badge> : '') },
    { key: 'd', header: '', render: (e) => <Button variant="outline" onClick={() => setDetail(e)}>Ver detalle</Button> },
  ];

  return (
    <div>
      <Header title="Horas y recargos" subtitle="TurnoGeo" />
      <div className="space-y-4 p-4">
        <Card>
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => quick('week')}>Esta semana</Button>
              <Button variant="outline" onClick={() => quick('lastWeek')}>Semana pasada</Button>
              <Button variant="outline" onClick={() => quick('month')}>Este mes</Button>
              <Button variant="outline" onClick={() => quick('lastMonth')}>Mes pasado</Button>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Input label="Desde" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
              <Input label="Hasta" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
              <Select label="Persona" value={userId} onChange={(e) => setUserId(e.target.value)} options={[{ value: '', label: 'Todas' }, ...employees.map((e) => ({ value: e.userId, label: e.fullName }))]} />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={generate} isLoading={isLoading}>Generar reporte</Button>
              <Button variant="outline" onClick={exportExcel} disabled={!report}>Exportar a Excel</Button>
              {isAdmin && <Button variant="outline" onClick={() => setShowImport(true)}>Cargar marcaciones (Excel)</Button>}
              <Button variant="outline" onClick={() => setShowHow(true)}>¿Cómo se calcula?</Button>
            </div>
            <p className="text-xs text-ink-400">Máximo 93 días por consulta. Las semanas van de lunes a domingo y se comparan con el mínimo semanal.</p>
          </div>
        </Card>

        {report && (
          <>
            {report.warnings.length > 0 && (
              <Card><ul className="list-disc space-y-1 pl-5 text-sm text-amber-700">{report.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul></Card>
            )}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              <StatCard icon={<TimerIcon />} label="Horas netas (sin almuerzo)" value={n2(totals.net)} />
              <StatCard icon={<TimerIcon />} label="Horas extra" value={n2(totals.extra)} tone="info" />
              <StatCard icon={<ReportIcon />} label="Recargo nocturno 35 % (h)" value={n2(totals.night)} tone="info" />
              <StatCard icon={<ReportIcon />} label="Horas en festivo/domingo" value={n2(totals.festive)} tone="info" />
              <StatCard icon={<AuditIcon />} label="Personas bajo el mínimo" value={totals.below} tone={totals.below ? 'warning' : 'brand'} />
              <StatCard icon={<AuditIcon />} label="Turnos abiertos" value={totals.open} tone={totals.open ? 'critical' : 'brand'} />
            </div>
            {report.employees.length === 0 ? (
              <EmptyState icon="🕒" title="Sin datos en el rango" description="No hay empleados activos ni marcaciones entre esas fechas." />
            ) : (
              <DataTable columns={columns} rows={report.employees} rowKey={(e) => e.userId} pageSize={15} pageSizeOptions={[15, 30, 50]} mobile="cards" emptyTitle="Sin datos" />
            )}
          </>
        )}
      </div>

      <DetailModal row={detail} report={report} onClose={() => setDetail(null)} />
      {isAdmin && <ImportMarksModal open={showImport} onClose={() => setShowImport(false)} onDone={() => { if (report) generate(); }} />}
      <Modal open={showHow} onClose={() => setShowHow(false)} title="¿Cómo se calcula?">
        <ul className="list-disc space-y-2 pl-5 text-sm text-ink-700">
          <li>Un turno va de ENTRADA a SALIDA. Al tiempo total se le <b>descuenta el almuerzo</b> (lo marcado, o mínimo el almuerzo parametrizado; si no se marcó, el de la ventana del horario).</li>
          <li>Se suma por <b>semana (lunes a domingo)</b> y se compara con el mínimo semanal (42 h). Se muestra cuánto falta o sobra.</li>
          <li>Cada hora se clasifica en <b>diurna / nocturna</b> (la noche empieza a las 21:00 hasta el 24-dic-2025 y a las 19:00 desde el 25-dic-2025) y en <b>ordinaria / festiva</b> (festivo del calendario o domingo).</li>
          <li>Extra = lo trabajado por encima de lo programado en cada tipo de hora. El <b>recargo nocturno del 35 %</b> aplica a las horas ordinarias nocturnas programadas.</li>
          <li>Los factores (1,25, 1,75, dominical 75/80/90 %, divisor 230/220/210) son los de la ley vigente en cada fecha: Configuración &gt; Factores de recargo.</li>
          <li>Los valores en pesos son una <b>estimación</b> a partir del salario de la ficha; solo los ve el administrador.</li>
        </ul>
      </Modal>
    </div>
  );
}

function DetailModal({ row, report, onClose }: { row: EmployeeHours | null; report: HoursReport | null; onClose: () => void }) {
  const toast = useToast();
  const [days, setDays] = useState<HoursDayRow[] | null>(null);
  useEffect(() => {
    setDays(null);
    if (!row || !report) return;
    repo.getHoursReport({ from: report.from, to: report.to, userId: row.userId, detail: true })
      .then((r) => setDays(r.employees[0]?.days ?? []))
      .catch((e) => toast.show(e instanceof Error ? e.message : 'No se pudo cargar el detalle.', 'error'));
  }, [row?.userId]);
  if (!row) return null;
  return (
    <Modal open={!!row} onClose={onClose} title={row.fullName}>
      <div className="space-y-4">
        <div>
          <p className="mb-1 text-xs font-semibold uppercase text-ink-400">Semanas</p>
          <div className="overflow-auto rounded-lg border border-ink-100 text-sm">
            <table className="w-full">
              <thead className="bg-ink-50 text-left text-xs text-ink-500"><tr><th className="p-2">Semana</th><th className="p-2">Horas</th><th className="p-2">Falta / sobra</th><th className="p-2">Cumple</th></tr></thead>
              <tbody>
                {row.weeks.map((w) => (
                  <tr key={w.weekStart} className="border-t border-ink-50">
                    <td className="p-2">{w.weekStart} → {w.weekEnd}</td>
                    <td className="p-2">{n2(w.netHours)} / {w.minHours}</td>
                    <td className="p-2">{w.deficitHours > 0 ? `faltan ${n2(w.deficitHours)}` : w.excessHours > 0 ? `+${n2(w.excessHours)}` : '—'}</td>
                    <td className="p-2">{w.partial ? <Badge tone="neutral">Parcial</Badge> : w.meetsMin ? <Badge tone="success">Sí</Badge> : <Badge tone="warning">No</Badge>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div>
          <p className="mb-1 text-xs font-semibold uppercase text-ink-400">Días</p>
          {days === null ? <p className="text-sm text-ink-500">Cargando…</p> : days.length === 0 ? <p className="text-sm text-ink-500">Sin turnos en el rango.</p> : (
            <div className="max-h-80 overflow-auto rounded-lg border border-ink-100 text-sm">
              <table className="w-full">
                <thead className="sticky top-0 bg-ink-50 text-left text-xs text-ink-500"><tr><th className="p-2">Día</th><th className="p-2">Entrada–Salida</th><th className="p-2">Alm.</th><th className="p-2">Netas</th><th className="p-2">Extra</th><th className="p-2">Rec. noct.</th></tr></thead>
                <tbody>
                  {days.map((d) => (
                    <tr key={d.date + d.entry} className="border-t border-ink-50">
                      <td className="p-2">{DOW[d.dayOfWeek]} {d.date.slice(5)}{d.isFestive && <Badge tone="info">{d.holidayName ?? 'Domingo'}</Badge>}</td>
                      <td className="p-2">{d.entry}–{d.open ? <Badge tone="critical">abierto</Badge> : d.exit}</td>
                      <td className="p-2">{d.lunchMinutes}′{d.lunchAuto ? '*' : ''}</td>
                      <td className="p-2">{n2(d.netHours)}</td>
                      <td className="p-2">{d.extraTotal > 0 ? n2(d.extraTotal) : '—'}</td>
                      <td className="p-2">{d.nightSurchargeHours > 0 ? n2(d.nightSurchargeHours) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="mt-1 text-xs text-ink-400">* almuerzo descontado automáticamente (sin marcas de almuerzo).</p>
        </div>
        <Button variant="outline" fullWidth onClick={onClose}>Cerrar</Button>
      </div>
    </Modal>
  );
}
